import React from 'react';
import { KTCard, KTCardBody } from '../../../../_metronic/helpers';
import { useIntl } from 'react-intl';
import { useGetMonthlyBillingQuery } from '../../../store/services/apuestesAPI';
import type {
  BillingData
} from '../../../store/types/dashboard.types';
import Chart from 'react-apexcharts';

const AnalyticsCharts: React.FC = () => {
  const intl = useIntl();
  const { data: billingData, isLoading, error } = useGetMonthlyBillingQuery();

  // Función para procesar los datos de facturación mensual
  const processBillingData = (): BillingData[] => {
    if (!billingData?.data) {
      // Datos de ejemplo si no hay datos reales
      return [
        { month: 'Jan', amount: 25000, subscriptions: 150 },
        { month: 'Feb', amount: 28000, subscriptions: 165 },
        { month: 'Mar', amount: 32000, subscriptions: 180 },
        { month: 'Apr', amount: 35000, subscriptions: 195 },
        { month: 'May', amount: 38000, subscriptions: 210 },
        { month: 'Jun', amount: 42000, subscriptions: 225 },
        { month: 'Jul', amount: 45000, subscriptions: 240 },
        { month: 'Aug', amount: 48000, subscriptions: 255 },
        { month: 'Sep', amount: 52000, subscriptions: 270 },
        { month: 'Oct', amount: 55000, subscriptions: 285 },
        { month: 'Nov', amount: 58000, subscriptions: 300 },
        { month: 'Dec', amount: 62000, subscriptions: 315 }
      ];
    }

    // Procesar datos reales de la API
    return billingData.data.map((item: BillingData) => ({
      month: item.month || 'Unknown',
      amount: item.amount || 0,
      subscriptions: item.subscriptions || 0
    }));
  };

  const chartData = processBillingData();

  // Calcular totales y cambios
  const totalAmount = chartData.reduce((sum: number, item: any) => sum + item.amount, 0);
  const lastMonth = chartData[chartData.length - 1];
  const previousMonth = chartData[chartData.length - 2];
  const amountChange = previousMonth ? 
    ((lastMonth.amount - previousMonth.amount) / previousMonth.amount * 100).toFixed(1) : '0';

  // Debug logs

  if (isLoading) {
    return (
      <div className="row g-4">
        <div className="col-12">
          <KTCard className="h-100">
            <KTCardBody className="d-flex flex-center p-8">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="row g-4">
        <div className="col-12">
          <KTCard className="h-100">
            <KTCardBody className="d-flex flex-center p-8">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle text-warning fs-1 mb-3"></i>
                <div className="text-muted">Error al cargar los datos de facturación</div>
                <small className="text-muted">{JSON.stringify(error)}</small>
              </div>
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    );
  }

  return (
    <div className="row g-4">
      {/* Monthly Billing Chart */}
      <div className="col-12">
        <KTCard className="h-md-100">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold fs-3 mb-1 text-gray-800">Facturación Mensual</span>
              <span className="text-muted fw-semibold fs-7">{intl.formatMessage({ id: 'DASHBOARD.CHARTS.REVENUE_SUBSCRIPTIONS' })}</span>
            </h3>
          </div>
          <KTCardBody className="pt-0">
            <div className="d-flex flex-wrap flex-stack pb-7">
              <div className="d-flex flex-column flex-grow-1 pe-8">
                <div className="d-flex align-items-center">
                  <div className="fs-4 fw-bold text-gray-800 me-2">
                    ${totalAmount.toLocaleString()}
                  </div>
                  <div className={`fs-7 fw-semibold ${parseFloat(amountChange) >= 0 ? 'text-success' : 'text-danger'}`}>
                    <i className={`fas fa-arrow-${parseFloat(amountChange) >= 0 ? 'up' : 'down'} fs-7`}></i>
                    {amountChange}%
                  </div>
                </div>
                <div className="fs-6 fw-semibold text-gray-400">{intl.formatMessage({ id: 'DASHBOARD.CHARTS.TOTAL_REVENUE' })}</div>
              </div>
              
              {/* Leyenda personalizada arriba a la derecha */}
              <div className="d-flex align-items-center">
                <div className="d-flex align-items-center gap-4">
                  <div className="d-flex align-items-center">
                    <div 
                      className="me-2" 
                      style={{
                        width: '14px',
                        height: '14px',
                        backgroundColor: '#50cd89',
                        borderRadius: '50%',
                        border: '3px solid #fff',
                        boxShadow: '0 3px 6px rgba(0,0,0,0.15)'
                      }}
                    ></div>
                    <span 
                      className="fs-7 fw-semibold px-4 py-2 text-white"
                      style={{
                        backgroundColor: '#50cd89',
                        borderRadius: '6px',
                        boxShadow: '0 0.5rem 1.5rem 0.5rem rgba(80, 205, 137, 0.175)'
                      }}
                    >
                      <i className="fas fa-dollar-sign me-1" style={{ color: '#fff' }}></i>
                      {intl.formatMessage({ id: 'DASHBOARD.CHARTS.REVENUE' })}
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <div 
                      className="me-2" 
                      style={{
                        width: '14px',
                        height: '14px',
                        backgroundColor: '#f1416c',
                        borderRadius: '50%',
                        border: '3px solid #fff',
                        boxShadow: '0 3px 6px rgba(0,0,0,0.15)'
                      }}
                    ></div>
                    <span 
                      className="fs-7 fw-semibold px-4 py-2 text-white"
                      style={{
                        backgroundColor: '#f1416c',
                        borderRadius: '6px',
                        boxShadow: '0 0.5rem 1.5rem 0.5rem rgba(241, 65, 108, 0.175)'
                      }}
                    >
                      <i className="fas fa-users me-1" style={{ color: '#fff' }}></i>
                      {intl.formatMessage({ id: 'DASHBOARD.CHARTS.SUBSCRIPTIONS' })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="chart-container">
              <Chart
                options={{
                  chart: {
                    id: 'monthly-billing-chart',
                    type: 'area',
                    height: 350,
                    background: 'transparent',
                    toolbar: {
                      show: false
                    },
                    animations: {
                      enabled: true,
                      easing: 'easeinout',
                      speed: 800
                    }
                  },
                  theme: {
                    mode: 'dark'
                  },
                  stroke: {
                    curve: 'smooth',
                    width: 3
                  },
                  fill: {
                    type: 'gradient',
                    gradient: {
                      shade: 'light',
                      type: 'vertical',
                      shadeIntensity: 0.5,
                      gradientToColors: ['rgba(80, 205, 137, 0.1)', 'rgba(241, 65, 108, 0.1)'],
                      inverseColors: false,
                      opacityFrom: 0.6,
                      opacityTo: 0.1,
                      stops: [0, 100]
                    }
                  },
                  colors: ['#50cd89', '#f1416c'],
                  xaxis: {
                    categories: chartData.map((item: any) => item.month),
                    title: {
                      text: intl.formatMessage({ id: 'DASHBOARD.CHARTS.MONTHS' }),
                      style: {
                        color: '#E4E6EF',
                        fontSize: '13px',
                        fontWeight: 600,
                        fontFamily: 'Poppins, sans-serif'
                      }
                    },
                    labels: {
                      style: {
                        colors: '#B5B5C3',
                        fontSize: '11px',
                        fontWeight: 500,
                        fontFamily: 'Poppins, sans-serif'
                      }
                    }
                  },
                  yaxis: [
                    {
                      title: {
                        text: intl.formatMessage({ id: 'DASHBOARD.CHARTS.REVENUE_DOLLARS' }),
                        style: {
                          color: '#E4E6EF',
                          fontSize: '13px',
                          fontWeight: 600,
                          fontFamily: 'Poppins, sans-serif'
                        }
                      },
                      labels: {
                        formatter: function (val) {
                          return '$' + val.toLocaleString();
                        },
                        style: {
                          colors: '#B5B5C3',
                          fontSize: '11px',
                          fontWeight: 500,
                          fontFamily: 'Poppins, sans-serif'
                        }
                      }
                    },
                    {
                      opposite: true,
                      title: {
                        text: intl.formatMessage({ id: 'DASHBOARD.CHARTS.SUBSCRIPTIONS' }),
                        style: {
                          color: '#E4E6EF',
                          fontSize: '13px',
                          fontWeight: 600,
                          fontFamily: 'Poppins, sans-serif'
                        }
                      },
                      labels: {
                        style: {
                          colors: '#B5B5C3',
                          fontSize: '11px',
                          fontWeight: 500,
                          fontFamily: 'Poppins, sans-serif'
                        }
                      }
                    }
                  ],
                  tooltip: {
                    shared: true,
                    intersect: false,
                    theme: 'dark',
                    style: {
                      fontSize: '13px',
                      fontFamily: 'Poppins, sans-serif'
                    },
                    fillSeriesColor: false,
                    marker: {
                      show: true
                    },
                    y: {
                      formatter: function (val, { seriesIndex }) {
                        if (seriesIndex === 0) {
                          return '$' + val.toLocaleString();
                        }
                        return val.toLocaleString();
                      }
                    }
                  },
                  legend: {
                    show: false
                  },
                  grid: {
                    show: false
                  },
                  plotOptions: {
                    area: {
                      fillTo: 'origin'
                    }
                  },
                  dataLabels: {
                    enabled: false
                  }
                }}
                series={[
                  {
                            name: intl.formatMessage({ id: 'DASHBOARD.CHARTS.REVENUE' }),
                    type: 'area',
                    data: chartData.map((item: any) => item.amount)
                  },
                  {
                    name: intl.formatMessage({ id: 'DASHBOARD.CHARTS.SUBSCRIPTIONS' }),
                    type: 'area',
                    data: chartData.map((item: any) => item.subscriptions)
                  }
                ]}
                type="area"
                height={350}
                className="apexcharts-canvas"
              />
            </div>
          </KTCardBody>
        </KTCard>
      </div>
    </div>
  );
};

export { AnalyticsCharts };
